import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  TextField,
} from "@mui/material";

export default function CustomTableMobile({
  columns = [],
  rowData = [],
  getRowId,
  actions = [],
  titleColField,
}) {
  const visibleColumns = useMemo(() => {
    return (columns || []).filter(
      (c) => !c?.hide && (c?.field || c?.headerName),
    );
  }, [columns]);

  const getValueByField = (row, field) => {
    if (!field) return undefined;
    return field.split(".").reduce((obj, key) => obj?.[key], row);
  };

  const resolveRawValue = (colDef, row, rowIndex) => {
    let value;

    if (typeof colDef.valueGetter === "function") {
      try {
        value = colDef.valueGetter({
          data: row,
          node: { rowIndex },
        });
      } catch {
        value = undefined;
      }
    } else if (colDef.field) {
      value = getValueByField(row, colDef.field);
    }

    return value;
  };

  const resolveDisplayValue = (colDef, row, rowIndex) => {
    const value = resolveRawValue(colDef, row, rowIndex);

    if (typeof colDef.valueFormatter === "function") {
      try {
        return colDef.valueFormatter({ value, data: row });
      } catch {
        // ignore
      }
    }

    if (typeof colDef.cellRenderer === "function") {
      try {
        return colDef.cellRenderer(
          {
            value,
            data: row,
            id: getRowId ? getRowId(row, rowIndex) : (row?.id ?? rowIndex),
            rowIndex,
          },
          { mobile: true },
        );
      } catch {
        // ignore
      }
    }

    if (value === null || value === undefined || value === "") return "—";
    return String(value);
  };

  const handleEditableChange = (row, rowIndex, col, newValue) => {
    if (!col?.field) return;

    const oldValue = getValueByField(row, col.field);

    const parsedValue =
      col.cellEditor === "agNumberCellEditor"
        ? newValue === ""
          ? ""
          : Number(newValue)
        : newValue;

    const newData = {
      ...row,
      [col.field]: parsedValue,
    };

    col.onCellValueChanged?.({
      data: newData,
      oldValue,
      newValue: parsedValue,
      rowIndex,
      colDef: col,
    });
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {rowData !== undefined && rowData.length === 0 && (
          <Typography textAlign={"center"}>
            داده‌ای جهت نمایش وجود ندارد
          </Typography>
        )}
        {rowData.map((row, index) => {
          const key = getRowId ? getRowId(row, index) : (row?.id ?? index);

          return (
            <Card key={String(key)} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ pb: 1.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {visibleColumns
                    .filter((c) => c?.headerName !== "ردیف")
                    .map((col, i) => {
                      const shouldSkip =
                        titleColField && col.field === titleColField;

                      if (shouldSkip) return null;

                      const label = col.headerName || col.field || "";
                      const rawValue = resolveRawValue(col, row, index);
                      const valueNode = resolveDisplayValue(col, row, index);

                      return (
                        <React.Fragment key={(col.field || label) + i}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "110px 1fr",
                              columnGap: 2,
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "Vazirmatn",
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {label}
                            </Typography>

                            <Box sx={{ textAlign: "left" }}>
                              {col.editable ? (
                                <TextField
                                  size="small"
                                  type={
                                    col.cellEditor === "agNumberCellEditor"
                                      ? "number"
                                      : "text"
                                  }
                                  value={rawValue ?? ""}
                                  onChange={(e) =>
                                    handleEditableChange(
                                      row,
                                      index,
                                      col,
                                      e.target.value,
                                    )
                                  }
                                  fullWidth
                                  inputProps={{
                                    inputMode:
                                      col.cellEditor === "agNumberCellEditor"
                                        ? "numeric"
                                        : undefined,
                                  }}
                                  sx={{
                                    "& input": {
                                      fontFamily: "Vazirmatn",
                                      textAlign: "left",
                                    },
                                  }}
                                />
                              ) : React.isValidElement(valueNode) ? (
                                valueNode
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: "Vazirmatn" }}
                                >
                                  {valueNode}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {i !== visibleColumns.length - 1 && (
                            <Divider sx={{ opacity: 0.5 }} />
                          )}
                        </React.Fragment>
                      );
                    })}
                </Box>

                {actions?.length ? (
                  <>
                    {/* <Divider sx={{ my: 1.5 }} /> */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      {actions.map((a, idx) => {
                        if (a.show && !a.show(row)) return null;

                        return (
                          <Button
                            key={idx}
                            size="small"
                            color={a.color || "primary"}
                            onClick={() => a.onClick(row)}
                            sx={{
                              fontFamily: "Vazirmatn",
                              fontWeight: 600,
                              textAlign: "center",
                              width: "100%",
                              marginTop: "10px",
                              marginBottom: "0px",
                            }}
                          >
                            {a.label}
                          </Button>
                        );
                      })}
                    </Box>
                  </>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
